import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Icon } from 'views/components/molecules/Icon';
import { InputText } from 'views/components/atoms/InputText';
import { Color, MAX_WIDTH } from 'views/constants';
import { Button } from 'views/components/atoms/Button';
import { FormattedMessage } from 'react-intl';
import { localeIds } from 'locals';
import { ErrorMessage } from 'views/components/molecules/ErrorMessage';

const InputAreaWrapper = styled.div`
  margin: 0;
  padding: 0 16px;
  background-color: ${Color.C6};
  border-bottom: 1px solid ${Color.C14};
`;

const InputWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0;

  &:not(:first-of-type) {
    border-top: 1px solid ${Color.C14};
  }
`;

interface InputAreaProps {
  defaultEmail?: string;
  setEmail(value: string): void;
  setPassword(value: string): void;
}

const InputArea: React.FC<InputAreaProps> = ({
  defaultEmail,
  setEmail,
  setPassword,
}) => (
  <InputAreaWrapper>
    <InputWrapper>
      <FormattedMessage id={localeIds.loginForm.email}>
        {(text: string): React.ReactElement => (
          <InputText
            label={<Icon type="mail" />}
            onChange={setEmail}
            placeholder={text}
            type="email"
            defaultValue={defaultEmail}
            required
          />
        )}
      </FormattedMessage>
    </InputWrapper>
    <InputWrapper>
      <FormattedMessage id={localeIds.loginForm.password}>
        {(text: string): React.ReactElement => (
          <InputText
            label={<Icon type="pass" />}
            onChange={setPassword}
            placeholder={text}
            type="password"
            required
          />
        )}
      </FormattedMessage>
    </InputWrapper>
  </InputAreaWrapper>
);

const MessageWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: ${MAX_WIDTH};
  margin: auto;
  padding-top: 16px;
`;

interface LoginFormProps {
  errorMessage?: string;
  disabled?: boolean;
  defaultEmail?: string;
  onLogin(email: string, password: string): void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  errorMessage,
  disabled = false,
  defaultEmail,
  onLogin,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleLogin = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onLogin(email, password);
  };

  return (
    <form onSubmit={handleLogin}>
      <InputArea
        defaultEmail={defaultEmail}
        setEmail={setEmail}
        setPassword={setPassword}
      />

      {errorMessage ? (
        <MessageWrapper>
          <ErrorMessage message={errorMessage} maxWidth={MAX_WIDTH} />
        </MessageWrapper>
      ) : null}

      <ButtonWrapper>
        <Button type="submit" style="primary" disabled={disabled}>
          <FormattedMessage id={localeIds.loginForm.submit} />
        </Button>
      </ButtonWrapper>
    </form>
  );
};
